import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import CoopLeaderboard from './CoopLeaderboard';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { isEditableTarget } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import {
    AQ_MOVE_REPEAT_MS, AqRoomSocket, SEAT_COLOR, SEAT_NAME, aqInviteLink, aqMoveForKey,
    aqRoomIdFromSearch, aqRooms, beaconLeaveAq, clearAqSeat, loadAqSeat, paintAqCoop, saveAqSeat,
    startStatus,
} from './aqCoopNet';
import { clearRoomParam, inviteRoomStatus, roomErrorMessage } from './onlineRoomsNet';

const GAME_ID = 'adventure_quest';
const ACCENT = GAME_ACCENT.adventure_quest;
const UPLOAD_URL = ARCADE.adventure_quest?.UPLOAD_URL;
const CANVAS_W = 960;
const CANVAS_H = 640;

const PANEL_SX = {
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(255,215,0,0.3)',
    bgcolor: 'rgba(16, 12, 0, 0.85)',
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
};

const CANVAS_STYLE = {
    border: '1px solid rgba(255,215,0,0.5)',
    borderRadius: '8px',
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: '100%',
    objectFit: 'contain',
    aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
};

const END_TEXT = {
    victory: 'Quest complete — the party reached the exit!',
    party_down: 'The whole party fell',
    time_up: 'Out of time',
    abandoned: 'Everyone left',
    cancelled: 'The host closed the room',
    no_show: 'Not everyone connected',
    lobby_timeout: 'The lobby timed out before the quest started',
    server_error: 'Server error — match stopped',
};

export function aqRoomIdFromLocation() {
    if (typeof window === 'undefined') return null;
    return aqRoomIdFromSearch(window.location.search);
}

/**
 * Adventure Quest Online Co-op (2–4 players): lobby with ready flags, the host
 * starts; one character per seat; the gateway judges every move, fight and
 * pickup. This page sends only its own move requests and draws snapshots.
 */
export default function AdventureQuestCoop({ headerContent, onLiveChange }) {
    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const [grant, setGrant] = useState(null);
    /** idle | waiting | ready | running | paused | finished */
    const [phase, setPhase] = useState('idle');
    const [conn, setConn] = useState('closed');
    const [message, setMessage] = useState('');
    const [result, setResult] = useState(null);
    const [seats, setSeats] = useState({});
    const [waitingRooms, setWaitingRooms] = useState([]);
    const [code, setCode] = useState(() => aqRoomIdFromLocation() || '');
    const [busy, setBusy] = useState(false);
    const [snap, setSnap] = useState(null);
    const [meta, setMeta] = useState(null);
    const [liveHost, setHostSeat] = useState(null); // host can move to another seat in the lobby

    const canvasRef = useRef(null);
    const endedRef = useRef(false);
    const sockRef = useRef(null);
    const grantRef = useRef(null);
    const repeatRef = useRef(null);
    const phaseRef = useRef(phase);
    phaseRef.current = phase;

    const live = phase === 'waiting' || phase === 'ready' || phase === 'running' || phase === 'paused';
    useEffect(() => { if (onLiveChange) onLiveChange(live); }, [live, onLiveChange]);

    const onMessage = useCallback((msg) => {
        switch (msg.type) {
            case 'welcome':
            case 'peer':
            case 'lobby':
            case 'start':
                if (msg.phase) setPhase(msg.phase);
                if (msg.host_seat) setHostSeat(msg.host_seat);
                if (msg.host_changed && msg.message) setMessage(msg.message);
                if (msg.seats) setSeats(msg.seats);
                if (msg.type === 'welcome') {
                    if (msg.meta) setMeta(msg.meta);
                    if (msg.snapshot) setSnap(msg.snapshot);
                }
                if (msg.type === 'start') setMessage('Quest started — explore together!');
                break;
            case 'state':
                setSnap(msg);
                if (msg.phase && msg.phase !== phaseRef.current) setPhase(msg.phase);
                break;
            case 'end':
                endedRef.current = true;
                setSnap(msg);
                setPhase('finished');
                setResult({ reason: msg.reason, team_score: msg.team_score });
                clearAqSeat();
                break;
            case 'kicked':
                setMessage(msg.reason === 'left' ? 'You left the party.' : 'This seat was opened in another tab.');
                break;
            case 'error':
                if (endedRef.current && msg.code === 'room_finished') break; // result already shown (incl. replayed end)
                if (msg.code === 'room_finished' || msg.code === 'room_not_found' || msg.code === 'bad_seat_token') {
                    setMessage('That room is no longer available.');
                    clearAqSeat();
                    setPhase('idle');
                    setGrant(null);
                } else if (msg.code) {
                    setMessage(`Server: ${msg.code.replace(/_/g, ' ')}`);
                }
                break;
            default:
                break;
        }
    }, []);

    const openSocket = useCallback((g) => {
        if (sockRef.current) sockRef.current.close();
        grantRef.current = g;
        setHostSeat(null);
        setGrant(g);
        setResult(null);
        setSnap(null);
        setPhase(g.phase || 'waiting');
        setSeats(g.seats || {});
        saveAqSeat(g);
        const s = new AqRoomSocket(g, {
            onMessage,
            onStatus: (st) => setConn(st),
            graceMs: (g.reconnect_grace_s || 20) * 1000,
        });
        sockRef.current = s;
        s.connect();
    }, [onMessage]);

    const run = useCallback(async (fn) => {
        setBusy(true);
        setMessage('');
        try {
            endedRef.current = false;
            openSocket(await fn());
        } catch (e) {
            setMessage(roomErrorMessage(e));
            clearRoomParam('aq_room');
        } finally {
            setBusy(false);
        }
    }, [openSocket]);

    const createRoom = () => run(() => aqRooms.create({}));
    const quickMatch = () => run(() => aqRooms.match({}));
    const joinRoom = (id) => run(() => aqRooms.join(String(id || code).trim()));

    const leave = useCallback(() => {
        const g = grantRef.current;
        if (g && phaseRef.current !== 'finished') beaconLeaveAq(g.room_id, g.seat_token);
        if (sockRef.current) sockRef.current.close();
        sockRef.current = null;
        grantRef.current = null;
        clearAqSeat();
        setGrant(null);
        setPhase('idle');
        setConn('closed');
    }, []);

    // Resume a seat after a reload (same tab, inside the grace window).
    useEffect(() => {
        const saved = loadAqSeat();
        if (saved && saved.room_id && saved.seat_token) {
            aqRooms.info(saved.room_id)
                .then((info) => {
                    if (info && info.phase !== 'finished') openSocket({ ...info, ...saved });
                    else clearAqSeat();
                })
                .catch(() => clearAqSeat());
        }
        return () => { if (sockRef.current) sockRef.current.close(); };
    }, [openSocket]);

    // Invite link (?aq_room=<id>): join once on load unless resuming a saved seat.
    const autoJoinRef = useRef(false);
    useEffect(() => {
        if (autoJoinRef.current) return;
        autoJoinRef.current = true;
        const id = aqRoomIdFromLocation();
        if (!id) return;
        const saved = loadAqSeat();
        if (saved && saved.room_id && saved.seat_token) return;
        inviteRoomStatus(aqRooms, id).then((st) => {
            if (st.join) { run(() => aqRooms.join(id)); return; }
            clearRoomParam('aq_room'); // a reload must not try this room again
            setMessage(st.message);
        });
    }, [run]);

    // Leave the lobby on tab close (a running match uses the grace window).
    useEffect(() => {
        const onHide = () => {
            const g = grantRef.current;
            if (g && phaseRef.current === 'waiting') beaconLeaveAq(g.room_id, g.seat_token);
        };
        window.addEventListener('pagehide', onHide);
        return () => window.removeEventListener('pagehide', onHide);
    }, []);

    // Open-room list while idle.
    useEffect(() => {
        if (phase !== 'idle') return undefined;
        let stop = false;
        const load = () => aqRooms.list().then((r) => { if (!stop) setWaitingRooms(r); }).catch(() => {});
        load();
        const id = setInterval(load, 4000);
        return () => { stop = true; clearInterval(id); };
    }, [phase]);

    // Draw (grid game: latest snapshot, no interpolation needed).
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
        if (ctx) paintAqCoop(ctx, meta, snap, { mySeat: grant?.seat });
    }, [meta, snap, grant]);

    // Keyboard → this character only; a held key repeats the move request.
    useEffect(() => {
        if (!grant) return undefined;
        const stop = () => {
            if (repeatRef.current) clearInterval(repeatRef.current.id);
            repeatRef.current = null;
        };
        const down = (e) => {
            if (isEditableTarget(e.target)) return;
            const mv = aqMoveForKey(e.key);
            if (!mv) return;
            e.preventDefault();
            if (e.repeat) return;
            if (sockRef.current) sockRef.current.sendAction(mv);
            stop();
            repeatRef.current = {
                key: e.key,
                id: setInterval(() => { if (sockRef.current) sockRef.current.sendAction(mv, { repeat: true }); }, AQ_MOVE_REPEAT_MS),
            };
        };
        // The press is always applied once (server-side); repeats are tagged and
        // release sends "stay", which clears a pending repeat (no overshoot).
        const up = (e) => {
            if (!repeatRef.current || repeatRef.current.key !== e.key) return;
            stop();
            if (sockRef.current) sockRef.current.sendAction('stay');
        };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        window.addEventListener('blur', stop);
        return () => {
            window.removeEventListener('keydown', down);
            window.removeEventListener('keyup', up);
            window.removeEventListener('blur', stop);
            stop();
        };
    }, [grant]);

    const mySeat = grant?.seat;
    const hostSeat = liveHost || grant?.host_seat || 'player_1';
    const isHost = mySeat === hostSeat;
    const mine = seats[mySeat] || {};
    const { canStart, reason: startReason } = startStatus(seats, mySeat, hostSeat, grant?.min_seats || 2);
    const link = grant ? aqInviteLink(grant.room_id) : '';
    const chars = snap?.characters || {};

    let banner = '';
    if (phase === 'waiting') banner = isHost ? (startReason || 'Everyone is ready — start the quest!') : (mine.ready ? 'Ready — waiting for the host to start.' : 'Press Ready when you are set.');
    else if (phase === 'ready') banner = 'Starting…';
    else if (phase === 'paused') banner = 'A player disconnected — waiting for them to come back…';
    else if (conn === 'reconnecting') banner = 'Connection lost — reconnecting…';

    const resultText = result
        ? `${END_TEXT[result.reason] || 'Game over'} · team score ${result.team_score ?? snap?.team_score ?? 0}`
        : '';

    return (
        <ArcadeGameShell
            title="Adventure"
            titleAccent="Quest"
            subtitle="Online Co-op — 2 to 4 players, one dungeon, the server judges every move"
            accentColor={ACCENT}
            headerContent={headerContent}
            boilerplate={GAME_BOILERPLATE.adventure_quest}
            session={session}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', gap: 1 }}>
                {!grant && (
                    <Box sx={PANEL_SX} data-testid="aq-coop-lobby">
                        <Typography sx={{ color: COLOR.text, fontWeight: 700, mb: 1 }}>Play co-op online (2–4 players)</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                            <Button variant="contained" onClick={createRoom} disabled={busy} sx={{ bgcolor: ACCENT, color: '#000' }}>
                                Create room
                            </Button>
                            <Button variant="outlined" onClick={quickMatch} disabled={busy}>Quick match</Button>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                label="Room code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) joinRoom(); }}
                                inputProps={{ 'aria-label': 'Room code' }}
                                sx={{ flex: 1 }}
                            />
                            <Button variant="outlined" onClick={() => joinRoom()} disabled={busy || !code.trim()}>Join</Button>
                        </Stack>
                        <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 0.5 }}>Open rooms</Typography>
                        {waitingRooms.length === 0 && (
                            <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.8rem' }}>
                                None right now — create one and send the link to your friends.
                            </Typography>
                        )}
                        <Stack spacing={0.5}>
                            {waitingRooms.map((r) => (
                                <Stack key={r.room_id} direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ color: COLOR.text, fontSize: '0.85rem', flex: 1 }}>
                                        {r.seats?.player_1?.username || 'Guest'} · {Object.keys(r.seats || {}).length}/{r.max_seats || 4} · {r.room_id}
                                    </Typography>
                                    <Button size="small" onClick={() => joinRoom(r.room_id)} disabled={busy}>Join</Button>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                )}
                {!grant && <CoopLeaderboard gameId="adventure_quest_coop" accentColor={ACCENT} />}

                {grant && (
                    <Box sx={{ ...PANEL_SX, py: 1.25 }} data-testid="aq-coop-status">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                            {Object.keys(seats).map((sid) => {
                                const s = seats[sid];
                                const c = chars[sid];
                                let label = `${SEAT_NAME[sid] || sid}${sid === mySeat ? ' (you)' : ''}${sid === hostSeat ? ' ★' : ''}`;
                                if (phase === 'waiting') label += sid === hostSeat ? '' : (s.ready ? ' · ready' : ' · not ready');
                                else if (c) label += c.retired ? ' · left' : (c.alive ? ` · ${c.health} hp` : ' · down');
                                if (!s.connected && !s.left) label += ' (offline)';
                                return (
                                    <Chip
                                        key={sid}
                                        size="small"
                                        variant="outlined"
                                        data-testid={`aq-seat-${sid}`}
                                        label={label}
                                        sx={{ color: SEAT_COLOR[sid], borderColor: SEAT_COLOR[sid] }}
                                    />
                                );
                            })}
                            {snap && phase !== 'waiting' && (
                                <>
                                    <Chip size="small" variant="outlined" data-testid="aq-coop-score" label={`Team ${snap.team_score}`} sx={{ color: '#FFD700' }} />
                                    <Chip size="small" variant="outlined" label={`${snap.time_left_s ?? '–'}s`} sx={{ color: COLOR.textMuted }} />
                                </>
                            )}
                            <Box sx={{ flex: 1 }} />
                            {phase === 'waiting' && !isHost && (
                                <Button size="small" variant={mine.ready ? 'outlined' : 'contained'} onClick={() => sockRef.current && sockRef.current.setReady(!mine.ready)}>
                                    {mine.ready ? 'Not ready' : 'Ready'}
                                </Button>
                            )}
                            {phase === 'waiting' && isHost && (
                                <Button size="small" variant="contained" disabled={!canStart} onClick={() => sockRef.current && sockRef.current.startMatch()} sx={{ bgcolor: ACCENT, color: '#000' }}>
                                    Start quest
                                </Button>
                            )}
                            <Button size="small" color="inherit" onClick={leave} sx={{ color: COLOR.textMuted }}>
                                {phase === 'finished' ? 'Back' : 'Leave'}
                            </Button>
                        </Stack>
                        {phase === 'waiting' && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
                                <Typography sx={{ color: COLOR.text, fontSize: '0.85rem' }}>
                                    Room code <b>{grant.room_id}</b>
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => { try { navigator.clipboard.writeText(link); setMessage('Invite link copied.'); } catch (_) { setMessage(link); } }}
                                >
                                    Copy invite link
                                </Button>
                            </Stack>
                        )}
                    </Box>
                )}

                {(banner || message || resultText) && (
                    <Typography role="status" sx={{ textAlign: 'center', color: resultText ? COLOR.text : COLOR.textMuted, fontWeight: resultText ? 700 : 400 }}>
                        {resultText || banner || message}
                    </Typography>
                )}

                {grant && (
                    <Box sx={{ p: 0.5, width: '100%', flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center' }}>
                        <canvas
                            ref={canvasRef}
                            data-testid="aq-coop-canvas"
                            width={CANVAS_W}
                            height={CANVAS_H}
                            role="img"
                            aria-label="Adventure Quest co-op dungeon"
                            style={CANVAS_STYLE}
                        />
                    </Box>
                )}
                <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.75rem', textAlign: 'center' }}>
                    Arrows / WASD move your character only · everyone still standing must reach the green exit · score is shared
                </Typography>
            </Box>
        </ArcadeGameShell>
    );
}
